import React, { useEffect, useRef } from 'react';
import { Copy, Trash2, Download, XCircle, CheckCircle } from 'lucide-react';
import { useConsoleStore } from '../../store/consoleStore';

interface Props {
  x: number;
  y: number;
  messageId?: string;
  onClose: () => void;
}

export const ConsoleContextMenu: React.FC<Props> = ({ x, y, messageId, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const copyMessage = useConsoleStore((state) => state.copyMessage);
  const clear = useConsoleStore((state) => state.clear);
  const clearErrors = useConsoleStore((state) => state.clearErrors);
  const exportLog = useConsoleStore((state) => state.exportLog);
  const selectMessage = useConsoleStore((state) => state.selectMessage);
  const messages = useConsoleStore((state) => state.messages);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Adjust position if menu would go off screen
  const adjustedX = Math.min(x, window.innerWidth - 200);
  const adjustedY = Math.min(y, window.innerHeight - 250);

  const handleCopyMessage = () => {
    if (messageId) {
      copyMessage(messageId);
    }
    onClose();
  };

  const handleCopyAll = () => {
    const allText = messages.map(m => m.message).join('\n');
    navigator.clipboard.writeText(allText);
    onClose();
  };

  const handleClear = () => {
    clear();
    onClose();
  };

  const handleClearErrors = () => {
    clearErrors();
    onClose();
  };

  const handleExport = () => {
    const logContent = exportLog();
    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `console-log-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    onClose();
  };

  const handleSelectAll = () => {
    // Select all messages (for copy purposes)
    selectMessage(null);
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className="fixed z-[1000] w-48 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl py-1"
      style={{ left: adjustedX, top: adjustedY }}
    >
      {messageId && (
        <>
          <button
            onClick={handleCopyMessage}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700 text-left"
          >
            <Copy size={14} />
            Copy message
          </button>
          <div className="h-px bg-zinc-700 my-1" />
        </>
      )}

      <button
        onClick={handleCopyAll}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700 text-left"
      >
        <Copy size={14} />
        Copy all
      </button>

      <button
        onClick={handleSelectAll}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700 text-left"
      >
        <CheckCircle size={14} />
        Select all
      </button>

      <div className="h-px bg-zinc-700 my-1" />

      <button
        onClick={handleClear}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700 text-left"
      >
        <Trash2 size={14} />
        Clear console
      </button>

      <button
        onClick={handleClearErrors}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700 text-left"
      >
        <XCircle size={14} />
        Clear errors only
      </button>

      <div className="h-px bg-zinc-700 my-1" />

      <button
        onClick={handleExport}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700 text-left"
      >
        <Download size={14} />
        Export log...
      </button>
    </div>
  );
};
